import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { useAuth, API } from "../App";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import {
  Bell, ArrowLeft, Heart, MessageCircle, Gavel, ShoppingBag, DollarSign,
  CheckCircle, XCircle, Eye, ExternalLink, Trash2, Package, Award,
  Clock, AlertCircle, Megaphone
} from "lucide-react";

const NOTIFICATION_ICONS = {
  like: Heart,
  comment: MessageCircle,
  bid: Gavel,
  outbid: Gavel,
  new_bid: Gavel,
  sold: DollarSign,
  message: MessageCircle,
  offer: DollarSign,
  counter_offer: DollarSign,
  counter_offer_expired: Clock,
  counter_offer_reminder: Clock,
  offer_accepted: CheckCircle,
  offer_rejected: XCircle,
  favorite_activity: Gavel,
  auction_won: Award,
  auction_lost: AlertCircle,
  auction_expired: Clock,
  purchase_confirmed: CheckCircle,
  sale_confirmed: CheckCircle,
  intent_accepted: CheckCircle,
  intent_rejected: XCircle,
  purchase_paid: CheckCircle,
  auction_sold: DollarSign,
  promo: Megaphone
};

const NOTIFICATION_COLORS = {
  like: "bg-red-100 text-red-600",
  comment: "bg-blue-100 text-blue-600",
  bid: "bg-green-100 text-green-600",
  outbid: "bg-amber-100 text-amber-600",
  new_bid: "bg-green-100 text-green-600",
  sold: "bg-green-100 text-green-600",
  message: "bg-purple-100 text-purple-600",
  offer: "bg-green-100 text-green-600",
  counter_offer: "bg-amber-100 text-amber-600",
  counter_offer_expired: "bg-gray-100 text-gray-600",
  counter_offer_reminder: "bg-orange-100 text-orange-600",
  offer_accepted: "bg-green-100 text-green-600",
  offer_rejected: "bg-red-100 text-red-600",
  favorite_activity: "bg-blue-100 text-blue-600",
  auction_won: "bg-green-100 text-green-600",
  auction_lost: "bg-amber-100 text-amber-600",
  auction_expired: "bg-gray-100 text-gray-600",
  purchase_confirmed: "bg-green-100 text-green-600",
  sale_confirmed: "bg-green-100 text-green-600",
  intent_accepted: "bg-green-100 text-green-600",
  intent_rejected: "bg-red-100 text-red-600",
  purchase_paid: "bg-green-100 text-green-600",
  auction_sold: "bg-green-100 text-green-600",
  promo: "bg-orange-100 text-orange-600"
};

const NOTIFICATION_LABELS = {
  like: "Me gusta",
  comment: "Comentario",
  bid: "Nueva oferta",
  outbid: "Oferta superada",
  new_bid: "Nueva oferta",
  sold: "Venta",
  message: "Mensaje",
  offer: "Oferta",
  counter_offer: "Contraoferta",
  counter_offer_expired: "Contraoferta expirada",
  counter_offer_reminder: "Contraoferta por vencer",
  offer_accepted: "Oferta aceptada",
  offer_rejected: "Oferta rechazada",
  favorite_activity: "Actividad",
  auction_won: "¡Ganaste!",
  auction_lost: "Subasta perdida",
  auction_expired: "Subasta finalizada",
  purchase_confirmed: "Compra confirmada",
  sale_confirmed: "Venta confirmada",
  intent_accepted: "Solicitud aceptada",
  intent_rejected: "Solicitud rechazada",
  purchase_paid: "Pago confirmado",
  auction_sold: "Subasta vendida",
  promo: "Anuncio de Clueer"
};

// Reusable notifications list - used both by the full /notifications page and the
// Header's emergent popup. Keep it single-column so it works inside a narrow sheet too.
export const NotificationsContent = ({ onClose }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchNotifications();
    markAsRead();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setNotifications(response.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const deleteNotification = async (notifId) => {
    try {
      await axios.delete(`${API}/notifications/${notifId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setNotifications((prev) => prev.filter((n) => n.notification_id !== notifId));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const getNotificationLink = (notif) => {
    if (notif.action_url) return notif.action_url;
    if (notif.type === "message") return "/messages";
    if (notif.product_id) return `/product/${notif.product_id}`;
    return null;
  };

  const getActions = (notif) => {
    const actions = [];
    const link = getNotificationLink(notif);
    if (link) actions.push({ label: "Ver detalles", icon: Eye, href: link });
    if (notif.product_id) actions.push({ label: "Ver producto", icon: Package, href: `/product/${notif.product_id}` });
    if (notif.type === "auction_won" || notif.type === "purchase_confirmed") {
      actions.push({ label: "Mis compras", icon: ShoppingBag, href: "/my-purchases" });
    }
    return actions;
  };

  const goTo = (href) => {
    onClose?.();
    navigate(href);
  };

  const goBack = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "all") return true;
    if (filter === "unread") return !n.read;
    if (filter === "purchases") return ["auction_won", "purchase_confirmed", "sale_confirmed", "offer_accepted"].includes(n.type);
    if (filter === "bids") return ["bid", "new_bid", "outbid"].includes(n.type);
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div data-testid="notifications-content">
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={goBack}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] transition-colors flex-shrink-0"
          data-testid="notifications-close-button"
        >
          <ArrowLeft size={18} className="text-[#64748B]" />
        </button>
        <div>
          <h2 className="text-lg font-bold text-[#0F172A]">Notificaciones</h2>
          <p className="text-xs text-[#64748B]">{unreadCount > 0 ? `${unreadCount} sin leer` : "Todas leídas"}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
        {[
          { id: "all", label: "Todas" },
          { id: "unread", label: "Sin leer", count: unreadCount },
          { id: "purchases", label: "Compras" },
          { id: "bids", label: "Subastas" }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              filter === f.id ? "bg-[#475569] text-white" : "bg-white border border-[#E2E8F0] text-[#64748B]"
            }`}
            data-testid={`notifications-filter-${f.id}`}
          >
            {f.label} {f.count !== undefined && f.count > 0 && `(${f.count})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="spinner"></div>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="text-center py-10" data-testid="empty-notifications">
          <Bell size={48} className="mx-auto text-[#CBD5E1] mb-3" />
          <p className="text-sm font-semibold text-[#0F172A]">No hay notificaciones</p>
          <p className="text-xs text-[#64748B]">
            {filter !== "all" ? "No hay notificaciones en esta categoría" : "Aquí aparecerán tus notificaciones"}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="notifications-list">
          {filteredNotifications.map((notif) => {
            const Icon = NOTIFICATION_ICONS[notif.type] || Bell;
            const colorClass = NOTIFICATION_COLORS[notif.type] || "bg-gray-100 text-gray-600";
            const label = NOTIFICATION_LABELS[notif.type] || "Notificación";
            const link = getNotificationLink(notif);
            const actions = getActions(notif);

            return (
              <Card
                key={notif.notification_id}
                className={notif.read ? "border-[#E2E8F0]" : "border-[#CBD5E1] bg-[#F8FAFC]"}
                data-testid={`notification-${notif.notification_id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <button
                      className="flex items-start gap-3 flex-1 min-w-0 text-left"
                      onClick={() => link && goTo(link)}
                    >
                      {notif.product_image ? (
                        <img src={notif.product_image} alt="Producto" className="w-11 h-11 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className={`p-2.5 rounded-xl ${colorClass} flex-shrink-0`}>
                          <Icon size={18} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${colorClass}`}>{label}</span>
                          {!notif.read && <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]"></span>}
                        </div>
                        <p className="text-sm text-[#0F172A] font-medium line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-[#64748B] mt-1">
                          {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true, locale: es })}
                        </p>
                      </div>
                    </button>
                    <button
                      onClick={() => deleteNotification(notif.notification_id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-[#94A3B8] hover:text-red-600 transition-colors flex-shrink-0"
                      data-testid={`delete-notification-${notif.notification_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {actions.length > 0 && (
                    <div className="flex gap-2 mt-2 pt-2 border-t border-[#F1F5F9]">
                      {actions.map((action, idx) => (
                        <button
                          key={idx}
                          onClick={() => goTo(action.href)}
                          className="flex items-center gap-1 text-xs font-semibold text-[#475569] hover:text-[#0F172A]"
                          data-testid={`notification-action-${notif.notification_id}-${idx}`}
                        >
                          <action.icon size={13} />
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsContent;
